import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { API_ENDPOINTS } from "../config/api";

const RESULTS_PER_PAGE = 100;
const HIDDEN_COLUMNS: string[] = [];

const SurveyResults: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [customerIdFilter, setCustomerIdFilter] = useState<string>("");

  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [backupBeforeDelete, setBackupBeforeDelete] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<string | null>(null);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const [startDate, setStartDate] = useState<string>(localStorage.getItem("surveyStartDate") || "");
  const [endDate, setEndDate] = useState<string>(localStorage.getItem("surveyEndDate") || "");
  const [selectedAudio, setSelectedAudio] = useState<string | null>(null);
  const [showAudioModal, setShowAudioModal] = useState(false);


  const fetchSurvey = async (start?: string, end?: string) => {
    const username = localStorage.getItem("username");
    const password = localStorage.getItem("password");

    if (!username || !password) {
      setError("Missing login credentials.");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setStatusMessage(null);

      const url = new URL(API_ENDPOINTS.surveyResponses);
      if (start) url.searchParams.append("start_date", start);
      if (end) url.searchParams.append("end_date", end);

      const res = await fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const json = await res.json();
      if (!Array.isArray(json)) throw new Error("Unexpected data format");

      if (json.length === 0 || !("call_started" in json[0])) {
        setStatusMessage("No survey responses yet.");
        setData([]);
        setFilteredData([]);
        return;
      }

      const sorted = json.sort(
        (a, b) => new Date(b.call_started).getTime() - new Date(a.call_started).getTime()
      );

      setData(sorted);
      setFilteredData(sorted);
    } catch (err: any) {
      console.error("Failed to fetch survey responses:", err);
      setError("Failed to fetch survey data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey(startDate, endDate);
  }, [startDate, endDate]); 

  useEffect(() => {
    localStorage.setItem("surveyStartDate", startDate);
    localStorage.setItem("surveyEndDate", endDate);
    fetchSurvey(startDate, endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    let filtered = [...data];
    if (statusFilter !== "All") {
      filtered = filtered.filter((row) => row.call_outcome_code === statusFilter);
    }
    if (customerIdFilter.trim() !== "") {
      filtered = filtered.filter((row) =>
        String(row.customer_id || "").toLowerCase().includes(customerIdFilter.toLowerCase())
      );
    }
    if (sortConfig) {
      filtered.sort((a, b) => {
        const valA = a[sortConfig.key] ?? "";
        const valB = b[sortConfig.key] ?? "";
        const compare = String(valA).localeCompare(String(valB), undefined, { numeric: true });
        return sortConfig.direction === "asc" ? compare : -compare;
      });
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  }, [statusFilter, sortConfig, customerIdFilter, data]);

  const handleViewTranscript = async (filename: string) => {
    try {
      // If it's already a presigned URL, use it directly
      if (filename.startsWith('http')) {
        const response = await fetch(filename);
        if (!response.ok) {
          throw new Error("Failed to fetch transcript from S3.");
        }
        const transcriptContent = await response.text();
        setSelectedTranscript(transcriptContent);
        setShowTranscriptModal(true);
        return;
      }
      
      // Fallback: Extract filename and use backend API
      let actualFilename = filename;
      if (filename.startsWith('http')) {
        const urlParts = filename.split('/');
        actualFilename = urlParts[urlParts.length - 1];
        actualFilename = actualFilename.split('?')[0];
      }
      
      // Get authentication credentials
      const username = localStorage.getItem("username");
      const password = localStorage.getItem("password");
      
      if (!username || !password) {
        throw new Error("Missing authentication credentials.");
      }
      
      // Use the backend API endpoint as fallback
      const response = await fetch(`/api/transcript?filename=${encodeURIComponent(actualFilename)}`, {
        headers: {
          "X-Username": username,
          "X-Password": password,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch transcript.");
      }
      const data = await response.json();
      const transcriptContent = data.file_content;
      setSelectedTranscript(transcriptContent);
      setShowTranscriptModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load transcript.");
    }
  };
  
  const handlePlayAudio = async (filename: string) => {
    try {
      // If it's already a presigned URL, use it directly
      if (filename.startsWith('http')) {
        const response = await fetch(filename);
        if (!response.ok) {
          throw new Error("Failed to fetch audio from S3.");
        }
        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        setSelectedAudio(audioUrl);
        setShowAudioModal(true);
        return;
      }
      
      // Fallback: Extract filename and use backend API
      let actualFilename = filename;
      if (filename.startsWith('http')) {
        const urlParts = filename.split('/');
        actualFilename = urlParts[urlParts.length - 1];
        actualFilename = actualFilename.split('?')[0];
      }
      
      // Get authentication credentials
      const username = localStorage.getItem("username");
      const password = localStorage.getItem("password");
      
      if (!username || !password) {
        throw new Error("Missing authentication credentials.");
      }
      
      // Use the backend API endpoint as fallback
      const response = await fetch(`/api/audio?filename=${encodeURIComponent(actualFilename)}`, {
        headers: {
          "X-Username": username,
          "X-Password": password,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch audio.");
      }
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      setSelectedAudio(audioUrl);
      setShowAudioModal(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load audio.");
    }
  };


  const handleCSVExport = () => {
    const headers = Object.keys(filteredData[0] || {});
    const csv = [headers.join(",")].concat(
      filteredData.map((row) =>
        headers.map((h) => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")
      )
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "survey_results.csv";
    link.click();
  };

  const handleConfirmClear = async () => {
    const username = localStorage.getItem("username");
    const password = localStorage.getItem("password");

    try {
      if (backupBeforeDelete) {
        const res = await fetch(`${API_ENDPOINTS.resetSurveyResponses}?backup=true`, {
          method: "POST",
          headers: {
            "X-Username": username ?? "",
            "X-Password": password ?? "",
          },
        });

        if (!res.ok || res.headers.get("content-type")?.includes("application/json")) {
          const error = await res.json().catch(() => ({}));
          throw new Error("ZIP creation failed: " + (error.error ?? "Unknown error"));
        }
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "survey_backup.zip";
        document.body.appendChild(link);
        link.click();

        if (link.parentNode === document.body) {
          document.body.removeChild(link); // ✅ safe
        }


        setTimeout(async () => {
          try {
            await finalizeDelete(username, password);
          } catch (err) {
            console.error(err);
            toast.error("❌ Final deletion failed.");
          }
        }, 1000);


      } else {
        await finalizeDelete(username, password);
      }
    } catch (e) {
      console.error(e);
      toast.error("❌ Backup or delete failed.");
    }
  };

  const finalizeDelete = async (username: string | null, password: string | null) => {
    const res = await fetch(API_ENDPOINTS.finalizeDelete, {
      method: "POST",
      headers: {
         "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) throw new Error("Finalize delete failed");
    toast.success(" Data deleted successfully");
    setShowConfirmModal(false);
    // 👇 Clear table immediately
    setData([]);
    setFilteredData([]);
    setStatusMessage("No survey responses yet.");
  };

  const uniqueStatuses = Array.from(new Set(data.map((row) => row.call_outcome_code))).filter(Boolean);

  return (
    <>
      <Toaster position="top-center" />
      <div className="max-w-7xl mx-auto bg-white p-6 rounded shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">📄 Survey Results</h2>
          <div className="space-x-2">
            <button onClick={handleCSVExport} className="bg-[#001d3d] text-white px-3 py-1 rounded text-sm">Download CSV</button>
            <button onClick={() => setShowConfirmModal(true)} className="bg-[#802dc8] text-white px-3 py-1 rounded text-sm">Clear Survey Responses</button>
          </div>
        </div>

        {/* 🔍 Combined Filters: Date, Status, Customer ID */}
        <div className="flex gap-4 mb-4 flex-wrap items-end">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-semibold">Start Date (by call start)</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border px-2 py-1 text-sm rounded"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-semibold">End Date (by call start)</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border px-2 py-1 text-sm rounded"
            />
          </div>

          {/* Filter by Status */}
          <div>
            <label className="block text-sm font-medium">Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border px-2 py-1 rounded text-sm w-48"
            >
              <option value="All">All</option>
              {uniqueStatuses.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {/* Search by Customer ID */}
          <div>
            <label className="block text-sm font-medium">Search by Customer ID:</label>
            <input
              type="text"
              value={customerIdFilter}
              onChange={(e) => setCustomerIdFilter(e.target.value)}
              className="border px-2 py-1 rounded text-sm w-64"
              placeholder="e.g. 12345"
            />
          </div>
        </div>


        {statusMessage && (
          <div className="text-sm text-gray-600 mb-4">
            {statusMessage}
          </div>
        )}
        {error && <div className="text-sm text-gray-800 mb-4">{error}</div>}

        {loading ? <div>Loading...</div> : (
          <div className="overflow-x-auto max-h-[75vh] border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  {Object.keys(filteredData[0] || {}).filter((key) => !HIDDEN_COLUMNS.includes(key)).map((key) => (
                    <th
                      key={key}
                      onClick={() => {
                        setSortConfig((prev) => {
                          if (prev?.key === key) {
                            return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
                          }
                          return { key, direction: "asc" };
                        });
                      }}
                      className="cursor-pointer px-3 py-2 text-left"
                    >
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData
                  .slice((currentPage - 1) * RESULTS_PER_PAGE, currentPage * RESULTS_PER_PAGE)
                  .map((row, idx) => (
                    <tr key={idx} className="border-t align-top">
                      {Object.entries(row)
                        .filter(([key]) => !HIDDEN_COLUMNS.includes(key))
                        .map(([key, cell], i) => (
                          <td key={i} className="px-3 py-1 whitespace-pre-wrap">
                            {key === "audio_file" && typeof cell === "string" && cell.startsWith("http") ? (
                              <button
                                onClick={() => handlePlayAudio(cell)} 
                                className="text-gray-600 underline px-2 py-1 hover:bg-blue-50 rounded"
                              >
                                🎵 Play Audio
                              </button>
                            ) : key === "transcript_file" && typeof cell === "string" && cell.startsWith("http") ? (
                              <button
                                onClick={() => handleViewTranscript(cell)}
                                className="text-gray-600 underline px-2 py-1 hover:bg-blue-50 rounded"
                              >
                                📄 View Transcript
                              </button>
                            ) : (
                              String(cell) // Default case to render cell as string
                            )}

                          </td>
                        ))}
                    </tr>
                  ))}
              </tbody>


            </table>
          </div>
        )}

        {filteredData.length > RESULTS_PER_PAGE && (
          <div className="flex justify-center mt-4 space-x-4">
            {Array.from({ length: Math.ceil(filteredData.length / RESULTS_PER_PAGE) }).map((_, idx) => (
              <button
                key={idx}
                className={`px-3 py-1 rounded ${currentPage === idx + 1 ? "bg-[#802dc8] text-white" : "bg-gray-200"}`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
          </div>
        )}

        {showConfirmModal && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
              <h2 className="text-xl font-bold mb-4 text-gray-800">Confirm Clear</h2>
              <p className="mb-4 text-gray-700">Are you sure you want to delete all survey responses?</p>
              <label className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={backupBeforeDelete}
                  onChange={(e) => setBackupBeforeDelete(e.target.checked)}
                  className="mr-2"
                />
                Download local backup before deleting
              </label>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isClearing}
                  className="bg-gray-200 px-4 py-2 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setIsClearing(true);
                    await handleConfirmClear();
                    setIsClearing(false);
                  }}
                  disabled={isClearing}
                  className="bg-[#ff4f58] text-white px-4 py-2 rounded flex items-center justify-center min-w-[100px]"
                >
                  {isClearing ? (
                    <>
                      <svg
                        className="w-4 h-4 mr-2 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4l5-5-5-5v4a12 12 0 100 24v-4l-5 5 5 5v-4a8 8 0 01-8-8z"
                        ></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTranscriptModal && selectedTranscript && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded max-w-2xl w-full h-[70vh] overflow-y-auto relative">
              <button
                onClick={() => setShowTranscriptModal(false)}
                className="absolute top-2 right-2 text-sm text-gray-600 border px-2 py-1 rounded"
              >
                Close
              </button>
              <h2 className="text-xl font-semibold mb-4 text-[#001d3d]">Transcript Preview</h2>
              <pre className="whitespace-pre-wrap overflow-auto text-sm text-gray-700 p-4 bg-gray-100 rounded">
                {selectedTranscript}
              </pre>
            </div>
          </div>
        )}
        {showAudioModal && selectedAudio && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded max-w-2xl w-full h-[30vh] overflow-y-auto relative">
              <button
                onClick={() => setShowAudioModal(false)}
                className="absolute top-2 right-2 text-sm text-gray-600 border px-2 py-1 rounded"
              >
                Close
              </button>
              <h2 className="text-xl font-semibold mb-4 text-[#001d3d]">Audio Player</h2>
              <audio controls src={selectedAudio} className="w-full">
                Your browser does not support the audio element.
              </audio>
            </div>
          </div>
        )}


      </div>
    </>
  );
};

export default SurveyResults;
